import { useCallback, useContext, useMemo } from 'react';

import { useWhiteboardPages, useSortedWhiteboardPages } from './useWhiteboardPages';
import { WhiteboardSessionContext } from '../context/WhiteboardSessionContext';
import { type Page } from '../types';

export const useWhiteboardPageNavigation = (onChangePageRequest: (newPage: Page) => void) => {
    const pages = useWhiteboardPages();
    const sortedPages = useSortedWhiteboardPages(pages);

    const { whiteboardSessionState } = useContext(WhiteboardSessionContext);
    const { currentPage } = whiteboardSessionState;

    const currentDocumentIndex = useMemo(() => {
        if (!currentPage || !sortedPages) {
            return;
        }

        return sortedPages.findIndex((page) => page.page_id === currentPage.page_id);
    }, [sortedPages, currentPage]);

    const pdfNavigationButtonsEnabled = useMemo(() => {
        if (currentDocumentIndex === undefined) {
            return {
                next: false,
                prev: false
            };
        }

        return {
            next: !!sortedPages[currentDocumentIndex + 1],
            prev: !!sortedPages[currentDocumentIndex - 1]
        };
    }, [currentDocumentIndex, sortedPages]);

    const onNextPageHandler = useCallback(() => {
        if (currentDocumentIndex === undefined) {
            return;
        }

        onChangePageRequest(sortedPages[currentDocumentIndex + 1]);
    }, [currentDocumentIndex, onChangePageRequest, sortedPages]);

    const onPrevPageHandler = useCallback(() => {
        if (currentDocumentIndex === undefined) {
            return;
        }

        onChangePageRequest(sortedPages[currentDocumentIndex - 1]);
    }, [currentDocumentIndex, onChangePageRequest, sortedPages]);

    return {
        onNextPageHandler: pdfNavigationButtonsEnabled.next ? onNextPageHandler : undefined,
        onPrevPageHandler: pdfNavigationButtonsEnabled.prev ? onPrevPageHandler : undefined
    };
};
